export const content = {
  de: `
# Die 3 Schritte zum idealen CRM-Setup: So läuft die Zusammenarbeit ab

Du überlegst, dein CRM-System zu optimieren? In diesem Artikel gebe ich dir einen detaillierten Einblick, wie eine Zusammenarbeit mit mir abläuft – von der ersten Analyse bis zur langfristigen Betreuung.

Nach über 60 erfolgreichen CRM-Implementierungen habe ich einen bewährten 3-Schritte-Prozess entwickelt, der sicherstellt, dass dein Vertrieb nicht nur technisch funktioniert, sondern auch kontinuierlich optimiert wird.

## Schritt 1: Kickoff & Datenmigration

Der erste Schritt ist entscheidend: Wir müssen deine bestehenden Daten sauber in das optimierte CRM-System migrieren.

### Was passiert im Kickoff-Call?

Im Kickoff-Call besprechen wir im Detail:

- **Deine aktuellen Lead-Quellen:** Woher kommen deine Leads? Online-Marketing, Kaltakquise, Empfehlungen?
- **Deinen Vertriebsprozess:** Wie ist dein aktueller Prozess aufgebaut? Welche Deal-Phasen nutzt ihr?
- **Bestehende Automatisierungen:** Was wird bereits automatisiert? Was fehlt noch?
- **Deine Ziele:** Was soll das neue System können? Wo liegen die größten Pain Points?

### Datenmigration: Keine Informationen gehen verloren

Egal ob deine Daten aktuell in Google Sheets, Excel oder einem anderen CRM-System liegen – ich kümmere mich um die komplette Migration.

**Was wird migriert?**

- ✅ Alle Kontakte und Leads
- ✅ Komplette Anrufhistorien
- ✅ Alle Notizen und Kommentare
- ✅ E-Mail-Verläufe
- ✅ Deal-Status und Pipeline-Informationen

**Das Ergebnis:** Deine Vertriebler können am ersten Tag nahtlos weiterarbeiten – alle Informationen sind 1:1 im neuen System vorhanden.

## Schritt 2: Umsetzung & Setup (bis zu 14 Tage)

Nach dem Kickoff-Call geht's zur Umsetzung. Die Implementierung dauert in der Regel bis zu 14 Tage.

### Was passiert in der Umsetzungsphase?

Ich kümmere mich um:

- **Komplette Migration:** Alle Daten werden sauber ins neue System übertragen
- **CRM-Setup:** Das System wird nach unseren Spezifikationen eingerichtet
- **Automatisierungen:** Alle Sequenzen, Trigger und Workflows werden aufgesetzt
- **Tool-Verknüpfungen:** Kalender, E-Mail, Telefonie, Slack – alles wird integriert
- **Prozess-Implementierung:** Der optimierte Vertriebsprozess wird technisch umgesetzt

Du musst dich um nichts kümmern. Ich brauche nur Zugriff auf die relevanten Systeme – den Rest erledige ich.

## Schritt 3: Onboarding & Training

Das beste CRM-System nützt nichts, wenn dein Team nicht weiß, wie es funktioniert. Deshalb ist das Onboarding entscheidend.

### Onboarding für die Führungsebene

Im ersten Onboarding-Call erkläre ich der Führungsebene:

- **Setup-Übersicht:** Wie ist das System aufgebaut?
- **Anpassungen:** Wie könnt ihr Dinge selbst anpassen?
- **Kontrolle & Reporting:** Welche Dashboards gibt es? Wie kontrolliert ihr den Vertrieb?
- **Best Practices:** Wie nutzt ihr das System optimal?

## Bereit für dein optimiertes CRM-System?

Wenn du:

- ✅ Ein CRM-System willst, das wirklich zu deinem Vertriebsprozess passt
- ✅ Keine Zeit für technische Spielereien hast
- ✅ Einen Partner suchst, der langfristig an deiner Seite ist
- ✅ Deinen Vertrieb kontinuierlich optimieren willst

Dann lass uns sprechen.
  `,
  en: `
# The 3 Steps to the Ideal CRM Setup: How the Collaboration Works

Are you considering optimizing your CRM system? In this article, I'll give you a detailed insight into how a collaboration with me works – from the initial analysis to long-term support.

After over 60 successful CRM implementations, I've developed a proven 3-step process that ensures your sales not only works technically, but is also continuously optimized.

## Step 1: Kickoff & Data Migration

The first step is crucial: We need to cleanly migrate your existing data into the optimized CRM system.

### What happens in the kickoff call?

In the kickoff call, we discuss in detail:

- **Your current lead sources:** Where do your leads come from? Online marketing, cold calling, referrals?
- **Your sales process:** How is your current process structured? Which deal phases do you use?
- **Existing automations:** What is already automated? What's still missing?
- **Your goals:** What should the new system be able to do? Where are the biggest pain points?

### Data Migration: No Information Gets Lost

Whether your data is currently in Google Sheets, Excel, or another CRM system – I take care of the complete migration.

**What gets migrated?**

- ✅ All contacts and leads
- ✅ Complete call histories
- ✅ All notes and comments
- ✅ Email histories
- ✅ Deal status and pipeline information

**The result:** Your sales reps can continue working seamlessly on day one – all information is 1:1 in the new system.

## Step 2: Implementation & Setup (up to 14 Days)

After the kickoff call, we move to implementation. The implementation usually takes up to 14 days.

### What happens in the implementation phase?

I take care of:

- **Complete migration:** All data is cleanly transferred to the new system
- **CRM setup:** The system is set up according to our specifications
- **Automations:** All sequences, triggers and workflows are set up
- **Tool integrations:** Calendar, email, telephony, Slack – everything is integrated
- **Process implementation:** The optimized sales process is technically implemented

You don't have to worry about anything. I just need access to the relevant systems – I'll take care of the rest.

## Step 3: Onboarding & Training

The best CRM system is useless if your team doesn't know how it works. That's why onboarding is crucial.

### Onboarding for Leadership

In the first onboarding call, I explain to leadership:

- **Setup overview:** How is the system structured?
- **Customizations:** How can you adjust things yourself?
- **Control & reporting:** What dashboards are there? How do you control sales?
- **Best practices:** How do you use the system optimally?

## Ready for Your Optimized CRM System?

If you:

- ✅ Want a CRM system that really fits your sales process
- ✅ Don't have time for technical tinkering
- ✅ Are looking for a partner who will be by your side long-term
- ✅ Want to continuously optimize your sales

Then let's talk.
  `,
}
